import type { GetStaticPropsContext } from 'next';
import { useQuery } from '@sanity/react-loader';
import { groq } from 'next-sanity';

import {
  checkPageSchema,
  type NextPageData,
} from '#/lib/sanity/fetchers/helpers';
import {
  loadQuery,
  loadQueryOptions,
  type QueryResponseInitial,
} from '#/lib/sanity/loader';
import { homepage } from '#/lib/sanity/queries/homepage';
import { PageProps as PagePropsSchema } from '#/lib/sanity/queries/page';
import { SanityPage } from '#/lib/sanity/queries/pageProps';
import { site } from '#/lib/sanity/queries/site';
import { Page } from '#/components/Page';

export const config = {
  maxDuration: 30,
};

const homepagePropsQuery = groq`{
  'page': ${homepage},
  'site': ${site},
}`;

type HomepageQueryResult = Omit<
  NextPageData<typeof PagePropsSchema.shape.page>,
  'draftMode'
>;

export async function getStaticProps({ draftMode }: GetStaticPropsContext) {
  const initial = await loadQuery<HomepageQueryResult>(
    homepagePropsQuery,
    {},
    loadQueryOptions({ draftMode }),
  );
  const data = await checkPageSchema<typeof PagePropsSchema.shape.page>({
    schema: PagePropsSchema,
    result: initial.data,
    draftMode,
    context: { callerName: 'homepage getStaticProps' },
  });
  const props = { ...data, draftMode: !!draftMode };

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props: { ...props, initial, isHomepage: true } };
}

export default function Home(
  props: NextPageData<typeof SanityPage> & {
    initial: QueryResponseInitial<HomepageQueryResult>;
    isHomepage?: boolean;
  },
) {
  const { data } = useQuery<HomepageQueryResult>(
    homepagePropsQuery,
    {},
    {
      initial: props.initial,
    },
  );
  const page = data.page ?? props.page;

  return (
    <>
      <Page
        sections={page?.sections}
        isPreview={props.draftMode}
        sanitySource={
          page
            ? {
                documentId: page._id,
                documentType: 'page',
                path: 'sections',
              }
            : undefined
        }
      />
    </>
  );
}
